// Silent address backfill for wallets registered before a chain was supported.
//
// Deriving an address needs the mnemonic. For no-PIN users getSeed() resolves
// silently at boot; for PIN users the seed is server-key-locked, so we piggyback
// on the moments a seed/reauth is ALREADY in hand (any successful ReauthFlow,
// any send) — the user must never see an extra prompt for this.
//
// This was btc-specific, which meant the next chain needed its own copy of the
// same loop and its own set of call sites. It is keyed on the chain list now:
// every chain the wallet is missing gets filled, and a chain added later needs
// nothing here. A coin needs no backfill of its own — it lives on the EOA its
// chain's stablecoins already use.

import { SUPPORTED_CHAINS, type Chain } from '@incpt/shared';
import { addWalletAddress } from './wallet';

/** Ethereum is the reference: every wallet ever registered has one, so an index
 *  with an eth address and no X address is exactly a wallet predating X. */
const REFERENCE_CHAIN: Chain = 'eth';

type WalletShape = {
  addresses: Array<{ chain: string; derivationIndex?: number }>;
} | null;

/** Per chain, so one chain failing (or finishing) never blocks another. */
const settled = new Set<Chain>();
let inFlight = false;

function indicesFor(wallet: NonNullable<WalletShape>, chain: string): Set<number> {
  return new Set(
    wallet.addresses.filter((a) => a.chain === chain).map((a) => a.derivationIndex ?? 0),
  );
}

/** Derivation indices that have a reference address but none for `chain`. */
function missingIndices(wallet: NonNullable<WalletShape>, chain: Chain): number[] {
  if (chain === REFERENCE_CHAIN) return [];
  const have = indicesFor(wallet, chain);
  return [...indicesFor(wallet, REFERENCE_CHAIN)].filter((i) => !have.has(i));
}

/** Whether any supported chain is missing an address on this wallet. */
export function needsAddressBackfill(wallet: WalletShape): boolean {
  if (!wallet) return false;
  return SUPPORTED_CHAINS.some((chain) => missingIndices(wallet, chain).length > 0);
}

/**
 * Register an address for every (chain, derivationIndex) the wallet is missing.
 * Idempotent, throttled, NEVER throws — always safe to fire and forget.
 * "duplicate" (409) from a concurrent device counts as success.
 */
export async function ensureWalletAddresses(
  initData: string,
  mnemonic: string,
  wallet: WalletShape,
  invalidate?: () => void,
): Promise<void> {
  if (inFlight || !wallet || !mnemonic) return;
  if (!needsAddressBackfill(wallet)) return;

  inFlight = true;
  try {
    let added = false;
    for (const chain of SUPPORTED_CHAINS) {
      if (settled.has(chain)) continue;
      const missing = missingIndices(wallet, chain);
      if (missing.length === 0) {
        settled.add(chain);
        continue;
      }
      let chainComplete = true;
      for (const idx of missing) {
        try {
          await addWalletAddress(initData, mnemonic, chain, idx);
          added = true;
        } catch (e) {
          // Another device already registered this index — that's success.
          if (String(e).includes('duplicate')) {
            added = true;
            continue;
          }
          // Real failure (offline, server error): leave this chain unsettled so
          // a later hook (next reauth/send) retries it, and move on to the rest
          // rather than letting one chain's outage hold up the others.
          console.warn(`[addressBackfill] ${chain} add failed for index`, idx, e);
          chainComplete = false;
          break;
        }
      }
      if (chainComplete) settled.add(chain);
    }
    if (added) invalidate?.();
  } finally {
    inFlight = false;
  }
}
